import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle, X } from 'lucide-react';

interface StatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: 'success' | 'error';
  title: string;
  message: string;
  autoClose?: number; // milliseconds before auto-closing (0 = no auto-close)
}

const StatusModal: React.FC<StatusModalProps> = ({
  isOpen,
  onClose,
  status,
  title,
  message,
  autoClose = 4000
}) => {
  // Auto-close after timeout
  useEffect(() => {
    if (isOpen && autoClose > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoClose);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, onClose]);

  // Fechar com ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const isSuccess = status === 'success';
  const statusColor = isSuccess ? '#10b981' : '#ef4444';
  const Icon = isSuccess ? CheckCircle : AlertCircle;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop escuro */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                duration: 0.3,
                ease: 'easeOut'
              }}
              className="border-2 rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
              style={{
                backgroundColor: 'var(--bg-card)',
                borderColor: statusColor,
                boxShadow: `0 20px 60px -10px ${statusColor}30`
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Topo decorativo com cor */}
              <motion.div
                className="h-1 w-full"
                style={{ backgroundColor: statusColor }}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />

              {/* Conteúdo */}
              <div className="px-8 py-8 flex flex-col items-center text-center">
                {/* Ícone animado */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    duration: 0.4,
                    ease: 'easeOut',
                    delay: 0.1
                  }}
                  className="mb-6"
                >
                  <motion.div
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: isSuccess ? [0, 10, -10, 0] : [0]
                    }}
                    transition={{
                      duration: isSuccess ? 0.6 : 0,
                      ease: 'easeInOut'
                    }}
                  >
                    <Icon
                      className="h-16 w-16"
                      style={{ color: statusColor }}
                    />
                  </motion.div>
                </motion.div>

                {/* Título */}
                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  className="text-2xl font-bold mb-3"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {title}
                </motion.h2>

                {/* Mensagem */}
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  className="text-base mb-6 leading-relaxed"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {message}
                </motion.p>

                {/* Botão Fechar */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.4 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                  className="px-8 py-3 rounded-lg font-semibold transition-all duration-200"
                  style={{
                    backgroundColor: statusColor,
                    color: '#ffffff',
                    border: 'none'
                  }}
                >
                  {isSuccess ? 'Ótimo!' : 'Entendi'}
                </motion.button>

                {/* Indicador de progresso de auto-close */}
                {autoClose > 0 && (
                  <motion.div
                    className="mt-6 h-1 rounded-full"
                    style={{ backgroundColor: `${statusColor}20`, width: '100%' }}
                  >
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: statusColor }}
                      initial={{ scaleX: 1 }}
                      animate={{ scaleX: 0 }}
                      transition={{ duration: autoClose / 1000, ease: 'linear' }}
                      origin="left"
                    />
                  </motion.div>
                )}
              </div>

              {/* Botão fechar X no canto */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-lg transition-colors duration-200"
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <X className="h-5 w-5" />
              </motion.button>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default StatusModal;
